<template>
  <div class="drag-drop-upload">
    <div
      class="upload-area"
      :class="{ 'drag-over': isDragOver, 'has-file': hasFile }"
      @click="triggerFileInput"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div class="upload-content">
        <template v-if="hasFile">
          <!-- File Preview -->
          <div class="file-preview">
            <img
              v-if="isImage"
              :src="filePreviewUrl"
              :alt="fileName"
              class="preview-image"
            />
            <div v-else class="file-info">
              <v-icon size="48" color="primary">fal fa-file</v-icon>
              <div class="file-name">{{ fileName }}</div>
            </div>
            <v-btn
              icon
              small
              color="error"
              class="remove-btn"
              @click.stop="removeFile"
            >
              <v-icon>fal fa-times</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-else>
          <!-- Upload Interface -->
          <div class="upload-interface">
            <v-icon size="48" color="grey lighten-1" class="upload-icon">
              fal fa-cloud-upload
            </v-icon>
            <div class="upload-text">
              {{ $t("drag_drop_files_here") }}
            </div>
            <div class="upload-or">{{ $t("or") }}</div>
            <v-btn
              color="primary"
              outlined
              class="browse-btn"
              @click.stop="triggerFileInput"
            >
              {{ $t("browse_files") }}
            </v-btn>
          </div>
        </template>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      :accept="inputAccept"
      :multiple="multiple"
      style="display: none"
      @change="handleFileChange"
    />

    <!-- Error message -->
    <div v-if="errorMessage" class="error-message">
      <v-icon small color="error" class="mr-1"
        >fal fa-exclamation-circle</v-icon
      >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: "DragDropUpload",
  props: {
    value: {
      type: [File, String, null],
      default: null,
    },
    accept: {
      type: String,
      default: "image/*",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: 5 * 1024 * 1024, // 5MB default
    },
    maxSizeMB: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      isDragOver: false,
      errorMessage: "",
    };
  },
  computed: {
    // An explicit list (not image/*) makes iOS convert HEIC photos to JPEG on pick.
    inputAccept() {
      return this.accept === "image/*"
        ? "image/jpeg,image/png,image/gif,image/webp"
        : this.accept;
    },
    hasFile() {
      return !!this.value;
    },
    fileName() {
      if (!this.value) return "";
      if (typeof this.value === "string") {
        // Extract filename from URL
        return this.value.split("/").pop();
      }
      return this.value.name;
    },
    isImage() {
      if (!this.value) return false;
      if (typeof this.value === "string") {
        // Check for file extensions
        if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(this.value)) {
          return true;
        }

        // Check for common image hosting patterns
        const imagePatterns = [
          "/image/",
          "/images/",
          "/img/",
          "/photo/",
          "/photos/",
          "scene7.com",
          "cloudinary.com",
          "imgur.com",
          "flickr.com",
          "storeimages.cdn-apple.com",
          "as-images.apple.com",
          "cdn-apple.com",
          "amazonaws.com",
          "googleusercontent.com",
          "fbcdn.net",
          "fmt=jpeg",
          "fmt=png",
          "fmt=gif",
          "fmt=webp",
          "format=jpeg",
          "format=png",
          "format=gif",
          "format=webp",
          "type=image",
          "content-type=image",
        ];

        // Check if URL contains any image-related patterns
        if (imagePatterns.some((pattern) => this.value.includes(pattern))) {
          return true;
        }

        // Check if URL has image-like query parameters
        if (this.value.includes("wid=") && this.value.includes("hei=")) {
          return true;
        }

        // Check if URL contains common image CDN domains
        const imageDomains = [
          "cdn",
          "images",
          "img",
          "media",
          "static",
          "assets",
        ];

        if (imageDomains.some((domain) => this.value.includes(domain))) {
          return true;
        }

        return false;
      }
      return this.value.type.startsWith("image/");
    },
    filePreviewUrl() {
      if (!this.value) return "";
      if (typeof this.value === "string") {
        return this.value;
      }
      return URL.createObjectURL(this.value);
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleDragOver(event) {
      this.isDragOver = true;
      this.clearError();
    },

    handleDragLeave(event) {
      this.isDragOver = false;
    },

    handleDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files || []);
      if (files.length > 0) {
        this.processFiles(files);
      }
    },

    handleFileChange(event) {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) {
        this.processFiles(files);
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    processFiles(files) {
      if (this.multiple) {
        this.processMultipleFiles(files);
        return;
      }

      this.processFile(files[0]);
    },

    processMultipleFiles(files) {
      this.clearError();

      const validFiles = [];
      let firstError = "";

      files.forEach((file) => {
        const error = this.getFileValidationError(file);
        if (error) {
          if (!firstError) {
            firstError = error;
          }
          return;
        }
        validFiles.push(file);
      });

      if (!validFiles.length) {
        if (firstError) {
          this.setError(firstError);
        }
        return;
      }

      if (firstError) {
        this.setError(firstError);
      }

      this.$emit("files-selected", validFiles);
      validFiles.forEach((file) => {
        this.$emit("file-selected", file);
      });
    },

    getFileValidationError(file) {
      if (this.accept !== "*" && !this.isValidFileType(file)) {
        return this.$t("invalid_file_type");
      }

      if (file.size > this.maxSize) {
        return this.$t("file_too_large", { maxSizeMB: this.maxSizeMB });
      }

      return "";
    },

    processFile(file) {
      this.clearError();

      const error = this.getFileValidationError(file);
      if (error) {
        this.setError(error);
        return;
      }

      // Emit the file
      this.$emit("input", file);
      this.$emit("file-selected", file);
    },

    isValidFileType(file) {
      if (this.accept === "image/*") {
        return file.type.startsWith("image/") && !/hei[cf]/i.test(file.type);
      }
      if (this.accept === "*") {
        return true;
      }

      const acceptedTypes = this.accept.split(",").map((type) => type.trim());
      return acceptedTypes.some((type) => {
        if (type.endsWith("/*")) {
          const baseType = type.replace("/*", "");
          return file.type.startsWith(baseType);
        }
        return (
          file.type === type ||
          file.name.toLowerCase().endsWith(type.replace("*", ""))
        );
      });
    },

    removeFile() {
      this.$emit("input", null);
      this.$emit("file-removed");
      this.clearError();
    },

    setError(message) {
      this.errorMessage = message;
    },

    clearError() {
      this.errorMessage = "";
    },
  },

  beforeDestroy() {
    // Clean up object URL if it exists
    if (this.value && typeof this.value === "object" && this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
    }
  },
};
</script>

<style scoped>
.drag-drop-upload {
  width: 100%;
}

.upload-area {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
  background-color: #fafafa;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.upload-area:hover {
  border-color: #5b39c9;
  background-color: #f5f9ff;
}

.upload-area.drag-over {
  border-color: #5b39c9;
  background-color: #e3f2fd;
  transform: scale(1.02);
}

.upload-area.has-file {
  min-height: 150px;
  border-style: solid;
  border-color: rgba(91, 57, 201, 0.35);
  background-color: rgba(91, 57, 201, 0.06);
}

.upload-content {
  text-align: center;
  padding: 20px;
  width: 100%;
}

.upload-interface {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  margin-bottom: 8px;
}

.upload-text {
  font-size: 16px;
  font-weight: 500;
  color: #666;
}

.upload-or {
  font-size: 14px;
  color: #999;
}

.browse-btn {
  font-weight: 500;
  text-transform: none;
  border-radius: 8px;
  padding: 8px 24px;
}

.file-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.preview-image {
  max-width: 120px;
  max-height: 120px;
  border-radius: 8px;
  object-fit: contain;
}

.file-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.file-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  text-align: center;
  word-break: break-word;
  max-width: 200px;
}

.remove-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 2;
}

.error-message {
  margin-top: 8px;
  color: #f44336;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Dark mode */
.theme--dark .upload-area {
  border-color: #2f333a;
  background-color: #171b21;
}

.theme--dark .upload-area:hover {
  border-color: #7c5cea;
  background-color: rgba(91, 57, 201, 0.12);
}

.theme--dark .upload-area.drag-over {
  border-color: #7c5cea;
  background-color: rgba(91, 57, 201, 0.2);
}

.theme--dark .upload-area.has-file {
  border-color: rgba(124, 92, 234, 0.5);
  background-color: rgba(91, 57, 201, 0.12);
}

.theme--dark .upload-text,
.theme--dark .file-name {
  color: #e2e8f0;
}

.theme--dark .upload-or {
  color: #94a3b8;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .upload-area {
    min-height: 150px;
  }

  .upload-text {
    font-size: 14px;
  }

  .browse-btn {
    padding: 6px 20px;
    font-size: 14px;
  }
}
</style>
